// analytics.ts

import { store } from '../reduxToolkit/store';
import { UserRole } from '../types/Forms';
import { refreshTokens } from './api';

// type Dest = 'posthog' | 'http';
let destination = 'http';
let endpoint = `${process.env.REACT_APP_BACKEND_API}/api/analytics/events`;
const deviceId =
  localStorage.getItem('device_id') ||
  (() => {
    const id = crypto.randomUUID();
    localStorage.setItem('device_id', id);
    return id;
  })();

let inited = false;
let sessionId = null;
let lastHumanActivity = Date.now();
let keepAliveTimer = null;
const SESSION_IDLE_ROTATE_MS = 30 * 60 * 1000;
// batching
const buffer = [];
let flushTimer = null;
function getOrCreateSessionId() {
  const now = Date.now();
  const stored = sessionStorage.getItem('session_id');
  const lastSeen = Number(sessionStorage.getItem('session_last_seen') || '0');

  // rotate if no id OR idle too long
  if (!stored || !lastSeen || now - lastSeen > SESSION_IDLE_ROTATE_MS) {
    const sid = crypto.randomUUID();
    sessionStorage.setItem('session_id', sid);
    sessionStorage.setItem('session_last_seen', String(now));
    return sid;
  }

  // reuse existing
  sessionStorage.setItem('session_last_seen', String(now));
  return stored;
}

function markSessionSeen() {
  sessionStorage.setItem('session_last_seen', String(Date.now()));
}

const g = window;
g.__ANA__ = g.__ANA__ || { inited: false };

export function initAnalytics(opts = {}) {
  // gate: only authenticated students
  if (opts.userRole !== UserRole.STUDENT) return;
  if (inited) return;
  if (g.__ANA__.inited) return;
  g.__ANA__.inited = true;
  inited = true;

  destination = opts.dest ?? destination;
  endpoint = opts.endpoint ?? endpoint;

  // start a new authenticated session
  sessionId = sessionId = getOrCreateSessionId();

  // first event
  capture('session_start', {});

  // human-activity tracker
  ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'].forEach((evt) =>
    window.addEventListener(
      evt,
      () => {
        lastHumanActivity = Date.now();
      },
      { passive: true },
    ),
  );

  startKeepAlive();
}

export function stopAnalytics() {
  inited = false;
  if (keepAliveTimer) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
  sessionId = null;
  sessionStorage.removeItem('session_id');
}

export function capture(name, props = {}) {
  if (!inited || !sessionId) return;

  markSessionSeen();
  const evt = {
    client_event_id: crypto.randomUUID(),
    name,
    ts: new Date().toISOString(),
    device_id: deviceId,
    session_id: sessionId,
    props,
  };

  buffer.push(evt);
  scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = window.setTimeout(flush, 2000); // batch every 2s
}

async function flush() {
  const events = buffer.splice(0, buffer.length);
  flushTimer = null;
  if (!events.length) return;

  const body = JSON.stringify({ events });
  const sameOrigin = new URL(endpoint, window.location.href).origin === window.location.origin;

  // Use sendBeacon for same-origin, else use fetch
  if (navigator.sendBeacon && sameOrigin) {
    navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    return;
  }

  // Helper to send analytics request
  const sendAnalytics = async () => {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'include',
        keepalive: true,
      });
      if (response.status !== 401) return;
      // If 401, try to refresh tokens
      const tokens = await refreshTokens();
      if (!tokens) return;

      // Retry once with new token
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'include',
        keepalive: true,
      });
    } catch (err) {
      // Optionally log error
      console.error('Analytics flush error:', err);
    }
  };

  sendAnalytics();
}

function startKeepAlive() {
  if (keepAliveTimer) return;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') capture('session_keepalive', { reason: 'hidden' });
  });

  keepAliveTimer = window.setInterval(() => {
    const active =
      document.visibilityState === 'visible' && Date.now() - lastHumanActivity < 90_000;
    capture('session_keepalive', { active });
  }, 120_000); // 2 minutes is plenty
}

window.addEventListener('beforeunload', () => flush());

// Video tracking
// Keep one binding per <video> element
const __boundVideos = new WeakSet();

export function bindVideoTracking(video, video_id, course_id, lesson_id, lessonTitle, courseName) {
  if (!video || __boundVideos.has(video)) {
    // already bound or invalid
    return () => {};
  }
  __boundVideos.add(video);

  // let hbTimer = null;
  // let lastHbAt = 0; // ms (performance.now())
  let seekFrom = null;
  let started = false; // initial play happened (for this src)
  let lastPlayFiredAt = 0;

  // const nowMs = () => performance.now();

  // const heartbeat = () => {
  //   if (document.visibilityState !== 'visible') return;
  //   if (video.paused || video.ended) return;

  //   const t = nowMs();
  //   const dt = lastHbAt ? Math.min(Math.max(t - lastHbAt, 0), 10000) : 5000; // clamp
  //   lastHbAt = t;

  //   capture('video_heartbeat', {
  //     video_id,
  //     course_id,
  //     lesson_id,
  //     lessonTitle,
  //     courseName,
  //     at: Math.floor(video.currentTime),
  //     watch_ms: Math.round(dt),
  //     rate: video.playbackRate,
  //     volume: Math.round(video.volume * 100),
  //     visible: true,
  //   });
  // };

  // const startHB = () => {
  //   if (!hbTimer) {
  //     lastHbAt = nowMs();
  //     hbTimer = window.setInterval(heartbeat, 5000);
  //   }
  // };

  // const stopHB = () => {
  //   if (hbTimer) {
  //     clearInterval(hbTimer);
  //     hbTimer = null;
  //   }
  //   lastHbAt = 0;
  // };

  // Event handlers
  const onPlay = () => {
    const now = Date.now();
    if (now - lastPlayFiredAt < 250) return;
    lastPlayFiredAt = now;

    // Decide initial vs resume
    const isInitial = !started && Math.floor(video.currentTime) <= 1;
    const kind = isInitial ? 'initial' : 'resume';

    if (isInitial) started = true;
    capture('video_play', {
      video_id,
      course_id,
      lesson_id,
      lessonTitle,
      courseName,
      at: Math.floor(video.currentTime),
      kind,
    });
    // startHB();
  };

  const onPause = (reason = 'pause') => {
    capture('video_pause', {
      video_id,
      course_id,
      lesson_id,
      lessonTitle,
      courseName,
      at: Math.floor(video.currentTime),
      reason,
    });
    // stopHB();
    started = false;
  };

  const onSeeking = () => {
    seekFrom = Math.floor(video.currentTime);
    capture('video_seek', {
      video_id,
      course_id,
      lesson_id,
      lessonTitle,
      courseName,
      from: seekFrom,
    });
  };

  const onSeeked = () => {
    const to = Math.floor(video.currentTime);
    if (seekFrom !== null && to !== seekFrom) {
      capture('video_seek', {
        video_id,
        course_id,
        lesson_id,
        lessonTitle,
        courseName,
        from: seekFrom,
        to,
      });
    }
    seekFrom = null;
  };

  const onRateChange = () => {
    capture('video_rate_change', {
      video_id,
      course_id,
      lesson_id,
      lessonTitle,
      courseName,
      rate: video.playbackRate,
    });
  };

  const onVolumeChange = () => {
    capture('video_volume_change', {
      video_id,
      course_id,
      lesson_id,
      lessonTitle,
      courseName,
      volume: Math.round(video.volume * 100),
    });
  };

  // const onVisibility = () => {
  //   if (document.visibilityState === 'hidden' && !video.paused && !video.ended) {
  //     onPause('hidden'); // treat hidden as a pause reason
  //   }
  // };

  const onBeforeUnload = () => {
    // stopHB();
  };

  // Wire up
  video.addEventListener('play', onPlay);
  video.addEventListener('pause', () => onPause('pause'));
  video.addEventListener('ended', () => onPause('ended'));
  video.addEventListener('seeking', onSeeking);
  video.addEventListener('seeked', onSeeked);
  video.addEventListener('ratechange', onRateChange);
  video.addEventListener('volumechange', onVolumeChange);
  // document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('beforeunload', onBeforeUnload);

  // Initial “open” event
  capture('video_tab_open', { video_id, course_id, lesson_id, lessonTitle, courseName });

  // Return a cleanup function
  const cleanup = () => {
    // stopHB();
    video.removeEventListener('play', onPlay);
    video.removeEventListener('pause', () => onPause('pause'));
    video.removeEventListener('ended', () => onPause('ended'));
    video.removeEventListener('seeking', onSeeking);
    video.removeEventListener('seeked', onSeeked);
    video.removeEventListener('ratechange', onRateChange);
    video.removeEventListener('volumechange', onVolumeChange);
    // document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('beforeunload', onBeforeUnload);
    __boundVideos.delete(video);
  };

  // Also stash it on the element for convenience
  video.__analyticsCleanup = cleanup;

  return cleanup;
}
